import { useState, useCallback } from 'react';

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';

export const useLLMSOperations = (token) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [previewData, setPreviewData] = useState({});
  const [error, setError] = useState(null);

  const setLoading = useCallback((key, loading) => {
    setLoadingStates(prev => ({ ...prev, [key]: loading }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const downloadLLMS = useCallback(async (type, identifier) => {
    const key = `${type}_${identifier}`;
    setLoading(key, true);
    setError(null);

    try {
      let url, filename;
      
      if (type === 'report') {
        url = `${BACKEND}/api/llms/${identifier}`;
        filename = 'llms.txt';
      } else if (type === 'domain') {
        url = `${BACKEND}/api/llms/domain`;
        filename = `llms-${identifier}.txt`;
      }

      const response = await fetch(url, {
        method: type === 'domain' ? 'POST' : 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        ...(type === 'domain' && {
          body: JSON.stringify({ domain: identifier })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };

    } catch (err) {
      setError(err.message);
      console.error('Download error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(key, false);
    }
  }, [token, setLoading]);

  const previewLLMS = useCallback(async (type, identifier) => {
    const key = `${type}_${identifier}`;
    setLoading(key, true);
    setError(null);

    try {
      let url;
      
      if (type === 'report') {
        url = `${BACKEND}/api/llms/${identifier}/preview`;
      } else if (type === 'domain') {
        url = `${BACKEND}/api/llms/domain/preview`;
      }

      const response = await fetch(url, {
        method: type === 'domain' ? 'POST' : 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        ...(type === 'domain' && {
          body: JSON.stringify({ domain: identifier })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Preview failed');
      }

      const data = await response.json();
      setPreviewData(prev => ({ ...prev, [key]: data }));

      return { success: true, data };

    } catch (err) {
      setError(err.message);
      console.error('Preview error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(key, false);
    }
  }, [token, setLoading]);

  const clearPreview = useCallback((key) => {
    setPreviewData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  }, []);

  const clearAllPreviews = useCallback(() => {
    setPreviewData({});
  }, []);

  return {
    loadingStates,
    previewData,
    error,
    setLoading,
    clearError,
    downloadLLMS,
    previewLLMS,
    clearPreview,
    clearAllPreviews,
  };
};
